/**
 * LayoutsWeb — cada tela do Design Studio tem um layout WEB próprio (decisão do dono, 2026-10-07).
 *
 * Até aqui só existia o mockup de celular, e ele era a referência também da web: no Picky V10
 * a web saiu como a tela de celular esticada. Aqui o dono vê quantas telas já têm layout web,
 * gera os que faltam (em pacotes — o progresso aparece a cada um) e alterna a visualização
 * entre celular e web.
 *
 *   - `ResumoDosLayoutsWeb`: "N de M telas com layout web" + "Gerar layouts web".
 *   - `SeletorDePlataforma`: Celular | Web para os mockups da lista.
 */
import { C } from '@/theme/palette';

export function contarComLayoutWeb(screens) {
  return (screens || []).filter((s) => s && s.layout_web_html).length;
}

/**
 * @param {{ screens: object[], onGerar: () => void, gerando?: boolean, avisos?: string[],
 *           erro?: string|null }} props
 */
export function ResumoDosLayoutsWeb({ screens, onGerar, gerando = false, avisos = [], erro = null }) {
  const total = (screens || []).length;
  if (!total) return null;
  const com = contarComLayoutWeb(screens);
  const faltam = total - com;
  return (
    <div data-testid="resumo-layouts-web" style={{ margin: '0 0 12px', padding: '10px 12px',
      borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12.5, display: 'flex',
      alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <strong style={{ color: C.text }}>
          {com.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} tela(s) com layout web
        </strong>
        <div style={{ color: faltam ? C.warn : C.dim, marginTop: 2 }}>
          {faltam
            ? `${faltam.toLocaleString('pt-BR')} tela(s) só têm o mockup de celular: na web, o codegen as recebe sem referência de desktop.`
            : 'Todas as telas têm a versão de celular e a de web.'}
        </div>
        {(avisos || []).filter(Boolean).length > 0 && (
          <ul role="alert" data-testid="avisos-layouts-web"
              style={{ margin: '6px 0 0', paddingLeft: 18, color: C.warn }}>
            {avisos.filter(Boolean).map((a) => <li key={a}>{a}</li>)}
          </ul>
        )}
        {erro && <div role="alert" style={{ marginTop: 6, color: C.digital }}>{String(erro)}</div>}
      </div>
      {faltam > 0 && (
        <button type="button" onClick={onGerar} disabled={gerando}
          aria-label={`Gerar o layout web de ${faltam.toLocaleString('pt-BR')} tela(s)`}
          style={{ cursor: gerando ? 'default' : 'pointer', background: 'transparent',
            color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 8,
            padding: '8px 14px', fontSize: 12.5, fontWeight: 700, opacity: gerando ? 0.6 : 1 }}>
          {gerando ? `Gerando… faltam ${faltam.toLocaleString('pt-BR')}` : '🖥 Gerar layouts web'}
        </button>
      )}
    </div>
  );
}

/** @param {{ valor: 'celular'|'web', onMudar: (v: string) => void }} props */
export function SeletorDePlataforma({ valor, onMudar }) {
  const opcoes = [['celular', '📱 Celular'], ['web', '🖥 Web']];
  return (
    <div role="group" aria-label="Plataforma dos mockups"
      style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }}>
      {opcoes.map(([id, rotulo]) => (
        <button key={id} type="button" aria-pressed={valor === id} onClick={() => onMudar(id)}
          style={{ cursor: 'pointer', border: 0, padding: '5px 11px', fontSize: 12, fontWeight: 600,
            background: valor === id ? C.accent : 'transparent',
            color: valor === id ? C.black : C.dim }}>
          {rotulo}
        </button>
      ))}
    </div>
  );
}

/** O HTML a mostrar para a tela na plataforma escolhida (null = não tem). */
export function layoutDaPlataforma(screen, plataforma) {
  return plataforma === 'web' ? (screen?.layout_web_html || null) : (screen?.layout_html || null);
}
